import { z } from "zod";
import { idSchema, optionalText } from "./fields";
import { emailSchema } from "./userSchema";

/** When an item applies. Most apply to both pickup and return. */
export const checklistPhaseSchema = z.enum(["pickup", "return", "both"]);
export type ChecklistPhase = z.infer<typeof checklistPhaseSchema>;

/** A checklist a borrower actually fills in. */
export const checklistStepSchema = z.enum(["pickup", "return"]);
export type ChecklistStep = z.infer<typeof checklistStepSchema>;

/** When the boat's notification email hears about a submitted checklist. */
export const checklistNotifySchema = z.enum(["never", "problems", "always"]);
export type ChecklistNotify = z.infer<typeof checklistNotifySchema>;

export const checklistItemSchema = z.object({
  id: idSchema,
  label: z.string(),
  phase: checklistPhaseSchema,
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

export const boatChecklistSchema = z.object({
  /** In the owner's chosen order. */
  items: z.array(checklistItemSchema),
  notify: checklistNotifySchema,
  notifyEmail: z.string().optional(),
});
export type BoatChecklist = z.infer<typeof boatChecklistSchema>;

/** The owner saves the whole list at once; array order is the checklist order. */
export const saveBoatChecklistSchema = z
  .object({
    boatId: idSchema,
    items: z
      .array(
        z.object({
          label: z
            .string()
            .trim()
            .min(1, "Every item needs text.")
            .max(200, "Keep items under 200 characters."),
          phase: checklistPhaseSchema,
        })
      )
      .max(60, "A checklist can have at most 60 items."),
    notify: checklistNotifySchema,
    notifyEmail: z.union([z.literal(""), emailSchema]).optional(),
  })
  .refine((c) => c.notify === "never" || Boolean(c.notifyEmail), {
    message: "Enter the email address to notify.",
    path: ["notifyEmail"],
  });
export type SaveBoatChecklist = z.infer<typeof saveBoatChecklistSchema>;

export const submitChecklistSchema = z.object({
  reservationId: idSchema,
  step: checklistStepSchema,
  results: z.array(
    z
      .object({
        itemId: idSchema,
        checked: z.boolean(),
        problemNote: optionalText(500),
      })
      .refine((r) => r.checked || r.problemNote !== undefined, {
        message: "Check off every item, or describe what's wrong with it.",
      })
  ),
  notes: optionalText(1000),
});
export type SubmitChecklist = z.infer<typeof submitChecklistSchema>;

export const checklistSubmissionSchema = z.object({
  step: checklistStepSchema,
  submittedAt: z.date(),
  submittedByName: z.string(),
  notes: z.string().optional(),
  hasProblems: z.boolean(),
  /** The items as they read when submitted. */
  items: z.array(
    z.object({
      label: z.string(),
      checked: z.boolean(),
      problemNote: z.string().optional(),
    })
  ),
});
export type ChecklistSubmission = z.infer<typeof checklistSubmissionSchema>;

/** A trip's pickup and return checklists: the current items, or what was submitted. */
export const tripChecklistsSchema = z.object({
  pickup: z.object({
    items: z.array(checklistItemSchema),
    submission: checklistSubmissionSchema.optional(),
  }),
  return: z.object({
    items: z.array(checklistItemSchema),
    submission: checklistSubmissionSchema.optional(),
  }),
});
export type TripChecklists = z.infer<typeof tripChecklistsSchema>;

/** The items a borrower checks at one step, in the owner's order. */
export const itemsForStep = <T extends { phase: ChecklistPhase }>(
  items: T[],
  step: ChecklistStep
): T[] => items.filter((item) => item.phase === step || item.phase === "both");
